import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, EmptyState, ErrorState, Input, PageHeader, Skeleton, Tabs } from '../../../components/ui';
import { useFetch } from '../../../hooks/useFetch';
import { money } from '../../../utils/currency';
import { reportService } from '../services/reportService';

const KINDS = {
  customers: { path: '/reports/customer-outstanding', label: 'To collect', party: '/customers', code: 'customerCode', billed: 'Invoiced', action: 'Record payment' },
  vendors: { path: '/reports/vendor-outstanding', label: 'To pay suppliers', party: '/vendors', code: 'vendorCode', billed: 'Purchases', action: 'Pay' },
};

export default function Outstanding() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('customers');
  const [search, setSearch] = useState('');
  const k = KINDS[tab];
  const { data, error, reload } = useFetch(() => reportService.get(k.path), [tab]);
  const rows = (data?.rows ?? []).filter((r) => !search || `${r.name} ${r.phone ?? ''} ${r[k.code] ?? ''}`.toLowerCase().includes(search.toLowerCase()));

  const pay = (r) =>
    tab === 'customers'
      ? navigate(`/transactions/new?category=invoice_payment&customerId=${r._id}`)
      : navigate(`/transactions/new?category=purchase_payment&vendorId=${r._id}`);

  return (
    <>
      <PageHeader title="Outstanding" subtitle="Who owes you, and whom you owe" />
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-line p-4 sm:flex-row sm:items-center sm:justify-between">
          <Tabs variant="pills" label="Outstanding type" value={tab} onChange={setTab} tabs={[{ value: 'customers', label: 'Customers' }, { value: 'vendors', label: 'Suppliers' }]} />
          <Input leftIcon="search" size="sm" type="search" placeholder="Search name or phone" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} containerClassName="sm:w-72" />
        </div>
        {data && (
          <div className="flex items-center justify-between border-b border-line bg-brand-50/60 px-5 py-4">
            <span className="text-sm font-medium text-navy-900">{k.label}</span>
            <span className="tabular text-2xl font-semibold text-brand-700">{money(data.total)}</span>
          </div>
        )}
        {error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : !data ? (
          <div className="space-y-2 p-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState icon="checkCircle" title="Nothing outstanding" message="Every account is settled." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="border-b border-line text-[13px] text-ink-500">
                <tr>
                  <th scope="col" className="px-5 py-3 font-normal">Name</th>
                  <th scope="col" className="px-5 py-3 font-normal">Phone</th>
                  <th scope="col" className="px-5 py-3 text-right font-normal">{k.billed}</th>
                  <th scope="col" className="px-5 py-3 text-right font-normal">Paid</th>
                  <th scope="col" className="px-5 py-3 text-right font-normal">Balance</th>
                  <th scope="col" className="px-5 py-3"><span className="sr-only">Action</span></th>
                </tr>
              </thead>
              <tbody className="tabular divide-y divide-line">
                {rows.map((r) => (
                  <tr key={r._id} className="hover:bg-navy-50/60">
                    <td className="px-5 py-3">
                      <Link to={`${k.party}/${r._id}`} className="font-medium text-navy-900 hover:text-brand-600">{r.name}</Link>
                      <p className="font-mono text-xs text-ink-400">{r[k.code]}</p>
                    </td>
                    <td className="px-5 py-3">{r.phone || '-'}</td>
                    <td className="px-5 py-3 text-right">{money(r.totalBilled)}</td>
                    <td className="px-5 py-3 text-right">{money(r.totalPaid)}</td>
                    <td className={`px-5 py-3 text-right font-semibold ${r.balance > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {r.balance < 0 ? `Advance ${money(-r.balance)}` : money(r.balance)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {r.balance > 0 && <Button size="sm" variant="outline" onClick={() => pay(r)}>{k.action}</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
